export const CANONICAL_GOAL_BLOCKED_ROLES = ['ASSISTANT', 'OFFICE', 'BILLING', 'REFERRAL_COORDINATOR'] as const;

export type DirectCanonicalGoalAction = 'ADD' | 'MODIFY' | 'MET' | 'DISCONTINUE';

export interface DirectCanonicalGoalRow {
  id: string;
  client_id: string;
  description: string;
  baseline_performance?: string | null;
  target_percentage?: number | null;
  current_percentage?: number | null;
  target_date?: string | null;
  status?: string | null;
  parent_goal_id?: string | null;
  origin_doc_id?: string | null;
}

export interface DirectCanonicalGoalInput {
  description?: string | null;
  baseline_performance?: string | null;
  target_percentage?: number | null;
  current_percentage?: number | null;
  target_date?: string | null;
}

export type DirectCanonicalGoalWrite =
  | { op: 'insert'; row: DirectCanonicalGoalRow }
  | { op: 'supersede'; id: string }
  | { op: 'status'; id: string; status: 'MET' | 'DISCONTINUED' };

export interface DirectCanonicalGoalPlan {
  writes: DirectCanonicalGoalWrite[];
  auditAction: 'ADD_CANONICAL_GOAL' | 'MODIFY_CANONICAL_GOAL' | 'MARK_GOAL_MET' | 'DISCONTINUE_CANONICAL_GOAL';
  auditEntityId: string;
  auditDetails: Record<string, unknown>;
}

export function isCurrentCanonicalGoalStatus(status: unknown): boolean {
  const value = String(status || '').trim().toUpperCase();
  return value === 'ACTIVE' || value === 'IN_PROGRESS';
}

export function isHistoricalCanonicalGoalStatus(status: unknown): boolean {
  const value = String(status || '').trim().toUpperCase();
  return value === 'MET' || value === 'DISCONTINUED' || value === 'SUPERSEDED' || value === 'REVISED';
}

/** Continue never changes a stored status. Historical rows stay closed. */
export function canonicalGoalStatusAfterContinue(status: unknown): string {
  return String(status || '');
}

export function canEditCanonicalGoals(input: {
  role?: string | null;
  providerRole?: string | null;
  providerCredential?: string | null;
}): boolean {
  const role = String(input.role || '').trim().toUpperCase();
  if ((CANONICAL_GOAL_BLOCKED_ROLES as readonly string[]).includes(role)) return false;
  const providerRole = String(input.providerRole || input.providerCredential || '').trim().toUpperCase();
  return providerRole === 'SLP';
}

function goalText(value: unknown): string {
  return String(value || '').trim();
}

function goalNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function planDirectCanonicalGoalChange(input: {
  clientId: string;
  action: DirectCanonicalGoalAction;
  existing?: DirectCanonicalGoalRow | null;
  fields?: DirectCanonicalGoalInput | null;
  newId: string;
}): { ok: true; plan: DirectCanonicalGoalPlan } | { ok: false; statusCode: number; error: string; code: string } {
  const clientId = goalText(input.clientId);
  const fields = input.fields || {};

  if (input.action === 'ADD') {
    const description = goalText(fields.description);
    if (!description) {
      return { ok: false, statusCode: 400, error: 'Goal text is required.', code: 'GOAL_TEXT_REQUIRED' };
    }
    const row: DirectCanonicalGoalRow = {
      id: input.newId,
      client_id: clientId,
      description,
      baseline_performance: goalText(fields.baseline_performance) || null,
      target_percentage: goalNumber(fields.target_percentage, 80),
      current_percentage: goalNumber(fields.current_percentage, 0),
      target_date: goalText(fields.target_date) || null,
      status: 'IN_PROGRESS',
      parent_goal_id: null,
      origin_doc_id: null
    };
    return {
      ok: true,
      plan: {
        writes: [{ op: 'insert', row }],
        auditAction: 'ADD_CANONICAL_GOAL',
        auditEntityId: row.id,
        auditDetails: { client_id: clientId, goal_id: row.id }
      }
    };
  }

  const existing = input.existing;
  if (!existing) {
    return { ok: false, statusCode: 404, error: 'Canonical goal not found.', code: 'GOAL_NOT_FOUND' };
  }
  if (goalText(existing.client_id) !== clientId) {
    return { ok: false, statusCode: 403, error: 'That goal belongs to a different client.', code: 'CROSS_CLIENT_GOAL' };
  }
  if (!isCurrentCanonicalGoalStatus(existing.status)) {
    return {
      ok: false,
      statusCode: 409,
      error: 'Only an active canonical goal can be changed this way.',
      code: 'GOAL_NOT_CURRENT'
    };
  }

  if (input.action === 'MODIFY') {
    const description = goalText(fields.description);
    if (!description) {
      return { ok: false, statusCode: 400, error: 'Goal text is required.', code: 'GOAL_TEXT_REQUIRED' };
    }
    const successor: DirectCanonicalGoalRow = {
      id: input.newId,
      client_id: clientId,
      description,
      baseline_performance: fields.baseline_performance !== undefined && fields.baseline_performance !== null
        ? goalText(fields.baseline_performance) || null
        : existing.baseline_performance || null,
      target_percentage: fields.target_percentage !== undefined && fields.target_percentage !== null
        ? goalNumber(fields.target_percentage, 80)
        : goalNumber(existing.target_percentage, 80),
      current_percentage: fields.current_percentage !== undefined && fields.current_percentage !== null
        ? goalNumber(fields.current_percentage, 0)
        : goalNumber(existing.current_percentage, 0),
      target_date: fields.target_date !== undefined && fields.target_date !== null
        ? goalText(fields.target_date) || null
        : existing.target_date || null,
      status: 'IN_PROGRESS',
      parent_goal_id: existing.id,
      origin_doc_id: null
    };
    return {
      ok: true,
      plan: {
        writes: [
          { op: 'supersede', id: existing.id },
          { op: 'insert', row: successor }
        ],
        auditAction: 'MODIFY_CANONICAL_GOAL',
        auditEntityId: successor.id,
        auditDetails: {
          client_id: clientId,
          previous_goal_id: existing.id,
          successor_goal_id: successor.id
        }
      }
    };
  }

  const status = input.action === 'MET' ? 'MET' : 'DISCONTINUED';
  return {
    ok: true,
    plan: {
      writes: [{ op: 'status', id: existing.id, status }],
      auditAction: input.action === 'MET' ? 'MARK_GOAL_MET' : 'DISCONTINUE_CANONICAL_GOAL',
      auditEntityId: existing.id,
      auditDetails: {
        client_id: clientId,
        goal_id: existing.id,
        previous_status: existing.status || null
      }
    }
  };
}

export function applyDirectCanonicalGoalPlan<T extends DirectCanonicalGoalRow>(
  goals: T[],
  plan: DirectCanonicalGoalPlan
): T[] {
  let next = goals.map((goal) => ({ ...goal }));
  for (const write of plan.writes) {
    if (write.op === 'supersede') {
      next = next.map((goal) => goal.id === write.id ? { ...goal, status: 'SUPERSEDED' } : goal);
    } else if (write.op === 'status') {
      next = next.map((goal) => goal.id === write.id ? { ...goal, status: write.status } : goal);
    } else {
      next = [...next, write.row as T];
    }
  }
  return next;
}
